import Swiper from "swiper/bundle";

// ================================================================
//  БЛОК ОТЗЫВОВ: видео-слайдер + вкладки
//
//  Концепция воспроизведения:
//  - Видео НЕ запускается автоматически никогда
//  - Запуск — только по клику пользователя на видео/кнопку play
//  - Safari (iOS + macOS) + Low Power Mode: play() вызывается
//    синхронно внутри обработчика события, без defer/await,
//    иначе браузер считает вызов не из жеста пользователя
//    и блокирует воспроизведение
// ================================================================
const initReviews = () => {
  const section = document.querySelector(".reviews");
  if (!section) return;

  const mediaCard = section.querySelector(".reviews__card--media");
  const sliderEl = section.querySelector(".js-reviews-slider");
  const tabs = section.querySelectorAll(".reviews__tab");
  const contents = section.querySelectorAll(".reviews__content");
  const tabsContainer = section.querySelector(".reviews__tabs");

  if (!sliderEl) return;

  // Зачищаем любые autoplay-атрибуты, которые могли остаться в HTML
  section.querySelectorAll("video").forEach((v) => {
    v.removeAttribute("autoplay");
    v.removeAttribute("autoPlay");
  });

  // ================================================================
  //  Утилиты
  // ================================================================

  /** @param {Element} slide */
  const getSlideVideo = (slide) => slide.querySelector("video");

  /**
   * Проверяет, что у видео задан src (не пустой и не равен href страницы).
   * @param {HTMLVideoElement} video
   * @returns {string|null}
   */
  const getVideoSrc = (video) => {
    if (video.src && video.src !== window.location.href) return video.src;
    const source = video.querySelector("source");
    return source?.src && source.src !== window.location.href
      ? source.src
      : null;
  };

  /** Пауза на всех видео в секции */
  const pauseAllVideos = () => {
    section.querySelectorAll("video").forEach((v) => {
      if (!v.paused) v.pause();
    });
  };

  /** Показать прелоадер/кнопку play на слайде */
  const showPreloader = (slide) => {
    const preloader = slide.querySelector(".js-video-preloader");
    if (preloader) preloader.classList.remove("is-hidden");
  };

  /** Скрыть прелоадер (видео играет) */
  const hidePreloader = (slide) => {
    const preloader = slide.querySelector(".js-video-preloader");
    if (preloader) preloader.classList.add("is-played", "is-hidden");
  };

  /** Показать прелоадер на всех слайдах */
  const resetAllPreloaders = () => {
    sliderEl
      .querySelectorAll(".swiper-slide")
      .forEach((s) => showPreloader(s));
  };

  // ================================================================
  //  Воспроизведение — Safari-совместимый вариант
  //
  //  Ключевые правила для Safari iOS / macOS / Low Power Mode:
  //  1. play() ОБЯЗАН вызываться синхронно внутри user-gesture handler
  //  2. Нельзя откладывать play() через setTimeout, Promise.then, canplay
  //  3. playsinline обязателен для iOS
  //  4. muted нужен только для autoplay; для user-initiated — не нужен
  // ================================================================
  /**
   * @param {Element} slide
   */
  const playSlideVideo = (slide) => {
    const video = getSlideVideo(slide);
    if (!video || !getVideoSrc(video)) return;

    // Запускаем play() немедленно — Safari требует синхронного вызова
    // в пользовательском жесте. Браузер сам начнёт загрузку, если нужно.
    const promise = video.play();

    if (promise !== undefined) {
      promise
        .then(() => hidePreloader(slide))
        .catch(() => {
          // NotAllowedError  — политика браузера / Low Power Mode
          // NotSupportedError — формат не поддерживается
          showPreloader(slide);
        });
    } else {
      // Старые браузеры возвращают undefined
      hidePreloader(slide);
    }
  };

  // ================================================================
  //  Swiper — видео-слайдер
  // ================================================================
  new Swiper(sliderEl, {
    slidesPerView: 1,
    allowTouchMove: true,
    speed: 400,
    navigation: {
      prevEl: ".js-reviews-prev",
      nextEl: ".js-reviews-next",
    },
    on: {
      slideChange() {
        // При смене слайда — пауза и возврат кнопки play
        pauseAllVideos();
        resetAllPreloaders();
      },
    },
  });

  // ================================================================
  //  Swiper — слайдер текстовых отзывов
  // ================================================================
  const textSliderEl = section.querySelector(".js-text-slider");
  if (textSliderEl) {
    new Swiper(textSliderEl, {
      slidesPerView: 1,
      allowTouchMove: true,
      speed: 400,
      navigation: {
        prevEl: ".js-text-prev",
        nextEl: ".js-text-next",
      },
    });
  }

  // ================================================================
  //  Клик по видео: play ↔ pause
  //  Важно: обработчик должен быть синхронным — play() внутри него
  //  считается user gesture на всех платформах
  // ================================================================
  sliderEl.querySelectorAll(".reviews__video-wrap").forEach((wrap) => {
    wrap.addEventListener("click", () => {
      const slide = wrap.closest(".swiper-slide");
      if (!slide) return;

      const video = getSlideVideo(slide);
      if (!video || !getVideoSrc(video)) return;

      if (video.paused) {
        playSlideVideo(slide);
      } else {
        video.pause();
        showPreloader(slide);
      }
    });
  });

  // ================================================================
  //  IntersectionObserver — пауза при уходе секции из зоны видимости
  //  (только пауза; автозапуск при возврате — намеренно отсутствует)
  // ================================================================
  if ("IntersectionObserver" in window && mediaCard) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            pauseAllVideos();
            resetAllPreloaders();
          }
        });
      },
      { threshold: 0.1 },
    );
    observer.observe(mediaCard);
  }

  // ================================================================
  //  Переключение вкладок
  // ================================================================
  tabs.forEach((tab) => {
    tab.addEventListener("click", () => {
      const targetTab = tab.dataset.tab;
      if (!targetTab) return;

      tabs.forEach((t) => {
        t.classList.remove("is-active");
        t.setAttribute("aria-pressed", "false");
      });
      tab.classList.add("is-active");
      tab.setAttribute("aria-pressed", "true");

      contents.forEach((c) => {
        c.classList.toggle("is-active", c.dataset.content === targetTab);
      });

      if (tabsContainer) {
        tabsContainer.classList.toggle(
          "reviews__tabs--text-active",
          targetTab === "text",
        );
      }

      // При переходе на вкладку «Текст» — пауза + возврат кнопок play
      if (targetTab !== "video") {
        pauseAllVideos();
        resetAllPreloaders();
      }
    });
  });
};

initReviews();
